import React from "react";
import { FileText, Scale, Users, AlertTriangle, Shield, CreditCard } from "lucide-react";

export default function TermsConditions() {

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-950 text-white relative overflow-hidden">
      {/* Animated Gradient Orbs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-purple-600/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-600/30 rounded-full blur-3xl animate-ping"></div>

      <div className="relative max-w-4xl mx-auto p-8 space-y-8 z-10">

        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-extrabold flex items-center justify-center gap-3 mb-4">
            <Scale className="w-10 h-10 text-purple-500 animate-bounce" />
            Terms & Conditions
          </h1>
          <p className="text-gray-400 text-lg">Last updated: October 6, 2025</p>
        </div>

        {/* Content Sections */}
        <div className="space-y-6">

          {/* Acceptance of Terms */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-purple-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <FileText className="w-6 h-6 text-purple-400" /> Acceptance of Terms
            </h2>
            <div className="space-y-3 text-gray-300">
              <p>By accessing and using Sama Music, you accept and agree to be bound by the terms and provision of this agreement.</p>
              <p>If you do not agree to abide by the above, please do not use this service.</p>
            </div>
          </div>

          {/* User Accounts */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-violet-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" /> User Accounts
            </h2>
            <div className="space-y-3 text-gray-300">
              <p><strong>Account Creation:</strong> You must provide accurate and complete information when creating an account.</p>
              <p><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
              <p><strong>Account Sharing:</strong> Sharing accounts is strictly prohibited and may result in termination.</p>
              <p><strong>Age Requirement:</strong> You must be at least 13 years old to use our service.</p>
            </div>
          </div>

          {/* Subscription & Payments */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-purple-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-purple-400" /> Subscription & Payments
            </h2>
            <div className="space-y-3 text-gray-300">
              <p><strong>Billing:</strong> Subscriptions are billed in advance on a recurring basis until canceled.</p>
              <p><strong>Price Changes:</strong> We reserve the right to change subscription prices with 30 days notice.</p>
              <p><strong>Failed Payments:</strong> Services may be suspended for accounts with failed payments.</p>
              <p><strong>Refunds:</strong> Refund requests are handled according to our refund policy.</p>
            </div>
          </div>

          {/* Content Usage */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-violet-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-400" /> Content Usage
            </h2>
            <div className="space-y-3 text-gray-300">
              <p><strong>License:</strong> You are granted a limited, non-exclusive license to access streamed content.</p>
              <p><strong>Restrictions:</strong> Downloading, copying, or redistributing content is prohibited.</p>
              <p><strong>Copyright:</strong> All content is protected by copyright and other intellectual property laws.</p>
              <p><strong>DMCA:</strong> We respond to valid DMCA takedown notices promptly.</p>
            </div>
          </div>

          {/* Prohibited Activities */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-purple-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-purple-400" /> Prohibited Activities
            </h2>
            <div className="space-y-3 text-gray-300">
              <p>You may not:</p>
              <p>• Use the service for any illegal or unauthorized purpose</p>
              <p>• Attempt to gain unauthorized access to our systems</p>
              <p>• Interfere with or disrupt the service or servers</p>
              <p>• Use automated tools to access the service excessively</p>
              <p>• Share your account credentials with others</p>
            </div>
          </div>

          {/* Service Availability */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-violet-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Scale className="w-6 h-6 text-purple-400" /> Service Availability
            </h2>
            <div className="space-y-3 text-gray-300">
              <p><strong>Uptime:</strong> We strive for 99.9% service availability but cannot guarantee uninterrupted service.</p>
              <p><strong>Maintenance:</strong> We may perform scheduled maintenance with advance notice.</p>
              <p><strong>Modifications:</strong> We reserve the right to modify or discontinue features with notice.</p>
              <p><strong>Geographic Restrictions:</strong> Service availability may vary by region.</p>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-purple-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-purple-400" /> Limitation of Liability
            </h2>
            <div className="space-y-3 text-gray-300">
              <p>Sama Music shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</p>
              <p>Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-violet-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" /> Contact Information
            </h2>
            <div className="space-y-3 text-gray-300">
              <p>For questions about these Terms & Conditions:</p>
            <p><strong>Email:</strong> samasong.music@gmail.com</p>
              <p><strong>Address:</strong> old market, Tawang, Arunachal Pradesh, 790104</p>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}